import { api } from '@libs/api';
import type {
  Edital,
  EditaisModalidades,
  OrgaoOption,
  OrgaosPromotores,
} from '@libs/types';
import { mapEditalExternal, mapListEnvelope } from '@libs/editaisMapper.utils';

/**
 * Serviços de domínio do jornal contra o LICITACOES (rule `http-api.md`).
 * `ApiRoutes` agrupa os caminhos relativos same-origin; o Bearer é anexado pelo
 * proxy do front (`server/apiProxy`), não aqui.
 *
 * Superfície de leitura EXCLUSIVA do jornal (ADR-010 / task_01):
 * - listagem/busca: `GET api/EditaisInternal/Jornal/Editais`
 * - detalhe:        `GET api/EditaisInternal/Jornal/Editais/InformacoesAdicionais/{uniqueId}`
 * - órgãos:         `GET api/EditaisInternal/Jornal/OrgaosPromotores`
 *
 * A autenticação (login de serviço) NÃO vive mais no cliente — o proxy do front
 * obtém e injeta o token no servidor.
 */
const ApiRoutes = {
  editais: {
    getEditais: (query = '') =>
      api.get(`api/EditaisInternal/Jornal/Editais${query}`),
    getEdital: (uniqueId: string) =>
      api.get(
        `api/EditaisInternal/Jornal/Editais/InformacoesAdicionais/${uniqueId}`,
      ),
    getOrgaosPromotores: (termo: string) =>
      api.get('api/EditaisInternal/Jornal/OrgaosPromotores', {
        params: termo ? { termo } : undefined,
      }),
    getModalidades: () => api.get('api/EditaisInternal/Modalidades'),
  },
};

/**
 * Modalidades disponíveis para o filtro (dropdown da Home). Rota
 * `GET api/Editais/Modalidades` (controller distinto do restante do jornal);
 * devolve a lista `EditaisModalidades` ({ id, name }), que a Home mapeia para as
 * opções do Select.
 */
export const getModalidades = (): Promise<EditaisModalidades[]> =>
  ApiRoutes.editais
    .getModalidades()
    .then((response) =>
      Promise.resolve((response.data as EditaisModalidades[]) ?? []),
    )
    .catch((e) => Promise.reject(e.response));

/**
 * Listagem paginada de editais publicados. `query` é a query string já montada
 * (`?Skip=...&Take=...` — o builder de filtros é da task_04). A resposta passa
 * pelo mapeador de envelope, devolvendo `{ content, count }`.
 */
export const getEditais = (
  query = '',
): Promise<{ content: Edital[]; count: number }> =>
  ApiRoutes.editais
    .getEditais(query)
    .then((response) => Promise.resolve(mapListEnvelope(response.data)))
    .catch((e) => Promise.reject(e.response));

/**
 * Detalhe do edital por `uniqueId` (identificador canônico). Mapeia o
 * `EditalExternalDto` para `Edital`, incluindo `documentos`/`documentosDoEdital`.
 */
export const getEdital = (uniqueId: string): Promise<Edital> =>
  ApiRoutes.editais
    .getEdital(uniqueId)
    .then((response) => Promise.resolve(mapEditalExternal(response.data)))
    .catch((e) => Promise.reject(e.response));

/**
 * Órgãos promotores com edital publicado (autocomplete + resolução nome→id).
 * A rota devolve `OrgaosPromotores` (nome/razaoSocial + orgaoPromotorId); aqui
 * achatamos para `OrgaoOption` ({ label, value }), que é o formato consumido pelo
 * autocomplete (`buildOrgaoSuggestions`) e pela resolução nome→id (`selectedOrgao`).
 * `value` é o `orgaoPromotorId` (Guid), enviado depois como `OrgaoPromotorId`.
 */
export const getOrgaosPromotores = (termo: string): Promise<OrgaoOption[]> =>
  ApiRoutes.editais
    .getOrgaosPromotores(termo)
    .then((response) => {
      const orgaos = (response.data as OrgaosPromotores[]) ?? [];
      const options: OrgaoOption[] = orgaos.map((orgao) => ({
        label: orgao?.nome || orgao?.razaoSocial || orgao?.nomeFantasia || '',
        value: orgao?.orgaoPromotorId || orgao?.uniqueId || '',
      }));
      return Promise.resolve(options);
    })
    .catch((e) => Promise.reject(e.response));

export default ApiRoutes;
